import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import { Step, StepLabel, Theme } from "@material-ui/core";
import { preapreTimeAgo } from "./calloutRequestedHelpers";
import clsx from "clsx";
import DoneIcon from "@material-ui/icons/Done";
import ICalloutRequestedStep from "./types/ICalloutRequestedStep";

import { etaStepId } from "./calloutRequestedData";

interface CalloutRequestedStepProps {
  step: ICalloutRequestedStep;
  activeStepId: number;
  isCalloutFinished: boolean;
  eta: string;
}

export interface StyleProps {
  isCalloutFinished: boolean;
}

const useStyles = makeStyles({
  labelContainer: {
    display: "flex",
  },
  labelWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  labelBlock: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 15,
    fontWeight: 700,
    fontFamily: "lato",
    color: "white",
    alignItems: "flex-start",

    "@media (max-width: 600px)": {
      justifyContent: "center",
    },
  },
  labelTitle: {
    fontSize: 22,

    "@media (max-width: 600px)": {
      fontSize: 15,
      fontWeight: "normal",
    },
  },
  timeAgo: {
    fontSize: 16,
    opacity: "0.4",

    "@media (max-width: 600px)": {
      fontSize: 13,
      fontWeight: "normal",
      marginTop: 1,
    },
  },
  finished: {
    opacity: "0.7",
  },
  etaBlock: {
    backgroundColor: "#3FA052",
    height: 29,
    width: 103,
    color: "#00152E",
    fontSize: 18,
    fontWeight: 700,
    fontFamily: "lato",
    borderRadius: 4,
    display: "inline-grid",
    marginTop: 10,
    alignItems: "center",

    "@media (max-width: 600px)": {
      height: 26,
      width: 78,
      fontSize: 14,
    },
  },
  stepIconBox: {
    backgroundColor: "white",
    opacity: "0.17",
  },
  stepIconBoxCompleted: {
    backgroundColor: "#3FA052",
  },
  stepIconBoxCalloutFinished: {
    backgroundColor: "white",
    opacity: "0.54",
  },
  stepIcon: {
    color: "#00152E",
    width: 30,
    height: 30,
    borderRadius: 30,
    padding: 12,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2,

    "@media (max-width: 600px)": {
      width: 23,
      height: 23,
      borderRadius: 30,
      padding: 6,
    },
  },
});

const isCompleted = (stepId: number, activeStepId: number): boolean => stepId < activeStepId;
const isActive = (stepId: number, activeStepId: number): boolean => stepId === activeStepId;

const CalloutRequestedStep: React.FC<CalloutRequestedStepProps> = (props) => {
  const { step, activeStepId, isCalloutFinished, eta, ...other } = props;
  const timer = React.useRef<any>(null);
  const [timeAgo, setTimeAgo] = React.useState("");
  const classes = useStyles();

  React.useEffect(() => {
    if (step.timeAgo) {
      timer.current = setInterval(() => {
        setTimeAgo(preapreTimeAgo(step.createdOn ?? ""));
      }, 1000);
    }
    return () => {
      if (timer && timer.current) {
        clearInterval(timer.current);
      }
    };
  }, [step.timeAgo]);

  return (
    <Step completed={isCompleted(step.id, activeStepId)} active={isActive(step.id, activeStepId)} {...other}>
      <StepLabel
        StepIconComponent={() => (
          <DoneIcon
            className={clsx(classes.stepIcon, {
              [classes.stepIconBoxCompleted]: step.id <= activeStepId,
              [classes.stepIconBox]: step.id > activeStepId,
              [classes.stepIconBoxCalloutFinished]: isCalloutFinished,
            })}
          />
        )}
        classes={{
          root: classes.labelContainer,
          label: classes.labelWrapper,
        }}
      >
        <div
          className={clsx(classes.labelBlock, {
            [classes.finished]: isCalloutFinished,
          })}
        >
          <div className={classes.labelTitle}>{`${step.name1} ${step.name2}`}</div>
          {timeAgo && <div className={classes.timeAgo}>{`${timeAgo} ago`}</div>}
        </div>
        {/* <div className={classes.etaBlock}>ETA 24:59</div> */}
        {etaStepId === step.id && isActive(step.id, activeStepId) && (
          <div className={classes.etaBlock}> {!eta ? "Calculating ETA..." : `ETA ${eta}`}</div>
        )}
      </StepLabel>
    </Step>
  );
};

export default CalloutRequestedStep;
